// UnifiedRequestsTable.tsx
import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import {
  Button,
  Tag,
  Typography,
  Space,
  Skeleton,
  Tooltip,
  Card,
  Divider,
  Empty,
  message,
  TableColumnsType,
  Popover,
  Tabs,
  Modal,
  Spin,
} from "antd";
import {
  CheckCircleOutlined,
  NumberOutlined,
  UserOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  ReloadOutlined,
  CreditCardOutlined,
  BellOutlined,
  HistoryOutlined,
  CopyOutlined,
  StopOutlined,
  CloseOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { RequestData, RequestStatus } from "./types";
import { TableFilters, FilterValues } from "./TableFilters";
import { useTranslation } from "react-i18next";
import { GlassTable } from "../../shared/components/GlassTable/GlassTable";
import { useRequestsStore } from "../../shared/stores/useRequestsStore";

import { getAndSanitize } from "../../shared/utils/sanitization";
import axios from "axios";
import { API_BASE_URL } from "../../constants/config";
import { useEffectiveAdminId, useAdminSelector } from "../../contexts/AdminSelectorContext";
import { useTheme } from "../../contexts/ThemeContext";
import { useUserPanel } from "../../contexts/UserPanelContext";
import { gamificationApi } from "../../shared/api/gamificationApi";
import { usePanelsStore } from "../../shared/stores/usePanelsStore";
import PanelSelector from "../../shared/components/PanelSelector";

export type RequestType = "LOAD" | "WITHDRAW";

export interface UnifiedRequestsTableProps {
  onCancelWithdraw: (request: RequestData) => void;
  processingRequestIds?: string[];
}

export const UnifiedRequestsTable: React.FC<UnifiedRequestsTableProps> =
  React.memo(
    ({
      onCancelWithdraw,
      processingRequestIds = [],
    }) => {
      const navigate = useNavigate();
      const {
        pendingRequests,
        approvedWithdraws,
        completedRejected,
        isLoading,
        fetchRequestsBatch,
        pagination,
      } = useRequestsStore();

      // Track previous filters to detect when they're cleared
      const prevFiltersRef = React.useRef<FilterValues>({});

      const { t } = useTranslation();
      const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
      const { theme } = useTheme();

      // Get effective admin ID for filtering (respects SuperAdmin admin selector)
      const effectiveAdminId = useEffectiveAdminId();
      const { isSuperAdmin } = useAdminSelector();

      // Active tab state
      const [activeTab, setActiveTab] = useState<'pending' | 'approved' | 'completed'>('pending');

      // Pagination state
      const [currentPage, setCurrentPage] = useState(1);
      const [pageSize, setPageSize] = useState(50);

      // Active filters state
      const [activeFilters, setActiveFilters] = useState<FilterValues>({});

      // Gamification data for context menu tab
      const [ctxGamificationData, setCtxGamificationData] = useState<{ data: any; amount: number } | null>(null);

      // Context menu state
      const [contextMenu, setContextMenu] = useState<{
        visible: boolean;
        x: number;
        y: number;
        record: RequestData | null;
      }>({ visible: false, x: 0, y: 0, record: null });
      const [contextRollover, setContextRollover] = useState<any>(null);
      const [isLoadingRollover, setIsLoadingRollover] = useState(false);
      const [contextBalance, setContextBalance] = useState<number | null>(null);
      const [isLoadingBalance, setIsLoadingBalance] = useState(false);
      const [contextHistoryData, setContextHistoryData] = useState<any[] | null>(null);
      const [isLoadingCtxHistory, setIsLoadingCtxHistory] = useState(false);
      const [ctxTab, setCtxTab] = useState<string>('info');
      const [panelSelectorOpen, setPanelSelectorOpen] = useState(false);
      const [availablePanels, setAvailablePanels] = useState<Array<{ id: string; name?: string; isActive?: boolean }>>([]);
      const [isLoadingPanels] = useState(false);
      const [pendingPanelOperation, setPendingPanelOperation] = useState<{
        type: 'balance' | 'history' | null;
        username: string | null;
      }>({ type: null, username: null });
      const { checkBalance, checkHistory } = useUserPanel();

      // Effect para detectar cambios en el tamaño de la ventana
      useEffect(() => {
        const handleResize = () => {
          setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
      }, []);

      // OPTIMIZED: Fetch all categories in a single batch request (reduces 3+ API calls to 1)
      useEffect(() => {
        const fetchData = async () => {
          try {
            // Check if there are any active filters (excluding empty/undefined values)
            const hasActiveFilters = Object.values(activeFilters).some(value =>
              value !== undefined && value !== null && value !== '' &&
              (Array.isArray(value) ? value.length > 0 : true)
            );

            // Check if filters were just cleared (previous had filters, now empty)
            const hadPreviousFilters = Object.keys(prevFiltersRef.current).length > 0;
            const filtersWereCleared = hadPreviousFilters && !hasActiveFilters;

            // Force refresh if:
            // 1. There are active filters, OR
            // 2. Filters were just cleared (to get fresh unfiltered data)
            const shouldForceRefresh = hasActiveFilters || filtersWereCleared;


            // Force refresh when filters are active or were just cleared
            await fetchRequestsBatch(
              currentPage,
              pageSize,
              activeFilters,
              shouldForceRefresh,
              effectiveAdminId
            );

            // Update previous filters ref
            prevFiltersRef.current = activeFilters;


          } catch (error) {
            console.error("[UnifiedRequestsTable] Error fetching batch data:", error);
            message.error(t("common.errors.loadingFailed"));
          }
        };

        fetchData();
      }, [currentPage, pageSize, activeFilters, fetchRequestsBatch, effectiveAdminId, t]);
      // Note: showPending, showApproved, showCompleted removed from dependencies
      // The batch always fetches all categories, but we filter display using these flags

      // Helper function to check if a request is being processed
      const isProcessing = useCallback(
        (record: RequestData) => {
          return (
            processingRequestIds.includes(record.id) ||
            record.status === "PROCESSING" ||
            (typeof record.isProcessing === "boolean" && record.isProcessing)
          );
        },
        [processingRequestIds]
      );

      // Get data based on active tab
      const combinedData = useMemo(() => {
        let data: RequestData[] = [];

        switch (activeTab) {
          case 'pending':
            data = pendingRequests as unknown as RequestData[];
            break;
          case 'approved':
            data = approvedWithdraws as unknown as RequestData[];
            break;
          case 'completed':
            data = completedRejected as unknown as RequestData[];
            break;
        }

        // Sort by creation date (newest first)
        return data.sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
      }, [
        pendingRequests,
        approvedWithdraws,
        completedRejected,
        activeTab,
      ]);

      // Calculate total count based on active tab
      const totalCount = useMemo(() => {
        switch (activeTab) {
          case 'pending':
            return pagination.pending.total;
          case 'approved':
            return pagination.approvedWithdraws.total;
          case 'completed':
            return pagination.completedRejected.total;
          default:
            return 0;
        }
      }, [
        activeTab,
        pagination.pending.total,
        pagination.approvedWithdraws.total,
        pagination.completedRejected.total,
      ]);

      const handleFilter = useCallback(
        (newFilters: FilterValues) => {

          // Check if we're clearing filters (going from filters to empty)



          // Reset to first page when filters change and update active filters
          // The useEffect will automatically fetch data with the new filters
          setCurrentPage(1);
          setActiveFilters(newFilters);
        },
        []
      );

      const handleTabChange = useCallback((key: string) => {
        setActiveTab(key as 'pending' | 'approved' | 'completed');
        // Reset to first page when tab changes
        setCurrentPage(1);
        // Clear filters when changing tabs
        setActiveFilters({});
      }, []);

      const handlePaginationChange = useCallback((page: number, newPageSize?: number) => {
        // Update pagination state - the useEffect will automatically fetch new data
        if (newPageSize && newPageSize !== pageSize) {
          setPageSize(newPageSize);
          setCurrentPage(1); // Reset to first page when page size changes
        } else {
          setCurrentPage(page);
        }
      }, [pageSize]);

      // === Context Menu Logic ===

      // Close context menu only on clicks OUTSIDE the panel
      useEffect(() => {
        if (!contextMenu.visible) return;
        const handleClickOutside = (e: MouseEvent) => {
          const target = e.target as HTMLElement;
          if (!target.closest('.ctx-menu-panel') && !target.closest('.ant-modal-root') && !target.closest('.ant-message')) {
            setContextMenu(prev => ({ ...prev, visible: false }));
          }
        };
        const handleKeyDown = (e: KeyboardEvent) => {
          if (e.key === 'Escape') setContextMenu(prev => ({ ...prev, visible: false }));
        };
        const timer = setTimeout(() => {
          document.addEventListener('mousedown', handleClickOutside);
          document.addEventListener('keydown', handleKeyDown);
        }, 0);
        return () => {
          clearTimeout(timer);
          document.removeEventListener('mousedown', handleClickOutside);
          document.removeEventListener('keydown', handleKeyDown);
        };
      }, [contextMenu.visible]);

      // Auto-fetch rollover + balance when context menu opens
      useEffect(() => {
        if (!contextMenu.visible || !contextMenu.record?.user) {
          setContextRollover(null);
          setContextBalance(null);
          setContextHistoryData(null);
          return;
        }
        const record = contextMenu.record;

        // Show cached balance immediately
        setContextBalance(record.user?.currentBalance ?? null);

        // Fetch rollover
        setIsLoadingRollover(true);
        gamificationApi.getUserActiveRollover(record.user.id)
          .then(data => setContextRollover(data))
          .catch(() => setContextRollover(null))
          .finally(() => setIsLoadingRollover(false));

        // Auto-fetch fresh balance
        setIsLoadingBalance(true);
        (async () => {
          try {
            const panels = await usePanelsStore.getState().fetchPanels();
            if (panels && panels.length > 0) {
              const result: any = await checkBalance({ username: record.user.username, panelId: panels[0].id });
              if (result?.balance !== undefined) {
                setContextBalance(result.balance);
              }
            }
          } catch {}
          setIsLoadingBalance(false);
        })();

        // Set cached history if available
        setContextHistoryData(record.user?.historyData || null);
      }, [contextMenu.visible, contextMenu.record?.user?.id]);

      const handleCtxRefreshBalance = useCallback(async () => {
        const record = contextMenu.record;
        if (!record?.user?.username) return;
        try {
          setIsLoadingBalance(true);
          const panels = await usePanelsStore.getState().fetchPanels();
          if (!panels || panels.length === 0) {
            message.error("No hay paneles disponibles");
            return;
          }
          if (panels.length === 1) {
            const result: any = await checkBalance({ username: record.user.username, panelId: panels[0].id });
            if (result?.balance !== undefined) setContextBalance(result.balance);
          } else {
            setAvailablePanels(panels);
            setPendingPanelOperation({ type: 'balance', username: record.user.username });
            setPanelSelectorOpen(true);
          }
        } catch {
          message.error("Error al verificar balance");
        } finally {
          setIsLoadingBalance(false);
        }
      }, [contextMenu.record, checkBalance]);

      const handleCtxUpdateHistory = useCallback(async () => {
        const record = contextMenu.record;
        if (!record?.user?.username) return;
        try {
          setIsLoadingCtxHistory(true);
          const panels = await usePanelsStore.getState().fetchPanels();
          if (!panels || panels.length === 0) {
            message.error("No hay paneles disponibles");
            return;
          }
          if (panels.length === 1) {
            const result: any = await checkHistory({ username: record.user.username, panelId: panels[0].id });
            setContextHistoryData(result?.history || []);
            setCtxTab('history');
          } else {
            setAvailablePanels(panels);
            setPendingPanelOperation({ type: 'history', username: record.user.username });
            setPanelSelectorOpen(true);
          }
        } catch {
          message.error("Error al actualizar historial");
        } finally {
          setIsLoadingCtxHistory(false);
        }
      }, [contextMenu.record, checkHistory]);

      const handleCtxCopyCredentials = useCallback(async () => {
        const record = contextMenu.record;
        if (!record?.user?.username) return;
        try {
          const token = getAndSanitize('token');
          let format = "Usuario: {username}\nClave: {password}";
          let adminId: string | null = null;
          if (token) {
            try {
              const decodedToken = JSON.parse(atob(token.split('.')[1]));
              adminId = decodedToken?.userId;
            } catch {}
          }
          if (adminId) {
            try {
              const settingsResponse = await axios.get(`${API_BASE_URL}/api/settings`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { adminId }
              });
              if (settingsResponse?.data?.success && settingsResponse?.data?.data?.credentialFormat) {
                format = settingsResponse.data.data.credentialFormat;
              } else if (settingsResponse?.data?.credentialFormat) {
                format = settingsResponse.data.credentialFormat;
              }
            } catch {}
          }
          const response = await axios.post(
            `${API_BASE_URL}/api/panel/user/credentials`,
            { username: record.user.username },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (response.data.success) {
            const formattedCredential = format
              .replace(/{username}/g, response.data.data.username)
              .replace(/{password}/g, response.data.data.password)
              .replace(/\busername\b/g, response.data.data.username)
              .replace(/\bpassword\b/g, response.data.data.password)
              .replace(/\\\\n/g, '\n')
              .replace(/\\n/g, '\n');
            await navigator.clipboard.writeText(formattedCredential);
            message.success("Credenciales copiadas");
          }
        } catch {
          message.error("Error al copiar credenciales");
        }
      }, [contextMenu.record]);

      const handleCtxCancelRollover = useCallback(() => {
        if (!contextRollover?.id) return;
        Modal.confirm({
          title: 'Cancelar Rollover',
          content: 'Se cancelará el requisito de rollover (apuestas sobre el bono). El usuario ya no necesitará cumplir esta condición para retirar. ¿Continuar?',
          okText: 'Cancelar Rollover',
          okButtonProps: { danger: true },
          cancelText: 'Volver',
          onOk: async () => {
            try {
              await gamificationApi.cancelRolloverRequirement(contextRollover.id);
              message.success('Requisito de rollover cancelado');
              // Refresh rollover data
              if (contextMenu.record?.user?.id) {
                const data = await gamificationApi.getUserActiveRollover(contextMenu.record.user.id);
                setContextRollover(data);
              }
            } catch (error: any) {
              message.error(error.response?.data?.message || 'Error al cancelar rollover');
            }
          },
        });
      }, [contextRollover, contextMenu.record]);

      const handleCtxCancelAntiLavado = useCallback(() => {
        if (!contextRollover?.id) return;
        Modal.confirm({
          title: 'Cancelar Condición de Retiro',
          content: 'Se cancelará la condición de retiro (antilavado). El usuario ya no necesitará cumplir esta condición para retirar. ¿Continuar?',
          okText: 'Cancelar Cond. Retiro',
          okButtonProps: { danger: true },
          cancelText: 'Volver',
          onOk: async () => {
            try {
              await gamificationApi.cancelAntiLavadoRequirement(contextRollover.id);
              message.success('Condición de retiro cancelada');
              if (contextMenu.record?.user?.id) {
                const data = await gamificationApi.getUserActiveRollover(contextMenu.record.user.id);
                setContextRollover(data);
              }
            } catch (error: any) {
              message.error(error.response?.data?.message || 'Error al cancelar condición');
            }
          },
        });
      }, [contextRollover, contextMenu.record]);

      const handleCtxPanelSelect = useCallback(async (panelId: string) => {
        setPanelSelectorOpen(false);
        if (!pendingPanelOperation.type || !pendingPanelOperation.username) return;
        try {
          if (pendingPanelOperation.type === 'balance') {
            setIsLoadingBalance(true);
            const result: any = await checkBalance({ username: pendingPanelOperation.username, panelId });
            if (result?.balance !== undefined) setContextBalance(result.balance);
          } else {
            setIsLoadingCtxHistory(true);
            const result: any = await checkHistory({ username: pendingPanelOperation.username, panelId });
            setContextHistoryData(result?.history || []);
            setCtxTab('history');
          }
        } catch {
          message.error(pendingPanelOperation.type === 'balance' ? "Error al verificar balance" : "Error al actualizar historial");
        } finally {
          setIsLoadingBalance(false);
          setIsLoadingCtxHistory(false);
          setPendingPanelOperation({ type: null, username: null });
        }
      }, [pendingPanelOperation, checkBalance, checkHistory]);

      // Render actions based on request type and status
      const renderActions = (record: RequestData) => {
        // SuperAdmin cannot validate/reject requests
        if (isSuperAdmin) {
          return null;
        }

        const processing = isProcessing(record);

        // Pending requests
        if (
          record.status === "REQUIRES_VALIDATION" ||
          record.status === "PENDING"
        ) {
          if (record.type === "LOAD") {
            return (
              <Button
                type="primary"
                size="small"
                icon={<CheckCircleOutlined />}
                onClick={() => navigate(`/user-panel/requests/${record.id}/validate`)}
                loading={processing}
              >
                {record.status === "REQUIRES_VALIDATION"
                  ? t("requestsPanel.pendingRequestsTable.buttons.validate")
                  : t("requestsPanel.pendingRequestsTable.buttons.process")}
              </Button>
            );
          }
          // WITHDRAW pending - navigate to withdraw page
          return (
            <Button
              type="primary"
              size="small"
              icon={<CheckCircleOutlined />}
              onClick={() => navigate(`/user-panel/requests/${record.id}/withdraw`)}
              loading={processing}
            >
              {t("requestsPanel.pendingRequestsTable.buttons.process")}
            </Button>
          );
        }

        // Approved withdrawals waiting for client validation
        if (record.status === "WAITING_FOR_CLIENT_VALIDATION") {
          return (
            <Button
              type="primary"
              danger
              size="small"
              onClick={() => onCancelWithdraw(record)}
            >
              {t("requestsPanel.approvedWithdrawalsTable.buttons.cancel")}
            </Button>
          );
        }

        // Approved withdrawals
        if (record.status === "APPROVED") {
          return (
            <Button
              type="primary"
              size="small"
              onClick={() => navigate(`/user-panel/requests/${record.id}/withdraw`)}
            >
              {t("requestsPanel.approvedWithdrawalsTable.buttons.complete")}
            </Button>
          );
        }

        // Rejected or canceled - can be reprocessed
        if (record.status === "REJECTED" || record.status === "CANCELED") {
          if (record.type === "LOAD") {
            return (
              <Button
                type="primary"
                size="small"
                icon={<ReloadOutlined />}
                onClick={() => navigate(`/user-panel/requests/${record.id}/validate`)}
              >
                {t("requestsPanel.completedRejectedTable.buttons.reprocess")}
              </Button>
            );
          }
          // WITHDRAW reprocess
          return (
            <Button
              type="primary"
              size="small"
              icon={<ReloadOutlined />}
              onClick={() => navigate(`/user-panel/requests/${record.id}/withdraw`)}
            >
              {t("requestsPanel.completedRejectedTable.buttons.reprocess")}
            </Button>
          );
        }

        // Deposit error - can retry
        if (record.status === "DEPOSIT_ERROR") {
          if (record.type === "LOAD") {
            return (
              <Tooltip title="Retry failed deposit">
                <Button
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={() => navigate(`/user-panel/requests/${record.id}/validate`)}
                >
                  {t("requestsPanel.completedRejectedTable.buttons.retryDeposit")}
                </Button>
              </Tooltip>
            );
          }
          return (
            <Tooltip title="Retry failed deposit">
              <Button
                size="small"
                icon={<ReloadOutlined />}
                onClick={() => navigate(`/user-panel/requests/${record.id}/withdraw`)}
              >
                {t("requestsPanel.completedRejectedTable.buttons.retryDeposit")}
              </Button>
            </Tooltip>
          );
        }

        // Completed - view details
        if (record.status === "COMPLETED") {
          return (
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/user-panel/requests/${record.id}/details`)}
            >
              Ver Detalles
            </Button>
          );
        }
        return null;
      };

      const columns = useMemo(
        () => {
          const baseColumns = [
            {
              title: "#",
              key: "requestNumber",
              width: activeTab === 'completed' ? 75 : 95,
              render: (record: RequestData) => (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  {activeTab !== 'completed' && (
                    <span
                      title={record.type === "WITHDRAW" ? "Retiro" : "Carga"}
                      style={{
                        display: "inline-block",
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        background: record.type === "WITHDRAW" ? "#722ed1" : "#1890ff",
                        flexShrink: 0,
                      }}
                    />
                  )}
                  {record.requestNumber ? (
                    <Typography.Text copyable>{record.requestNumber}</Typography.Text>
                  ) : null}
                </span>
              ),
            },
            {
              title: t("requestsPanel.pendingRequestsTable.table.columns.user"),
              key: "user",
              width: 260,
              render: (record: RequestData) => (
                <div style={{ whiteSpace: "nowrap" }}>
                  <Typography.Text strong>{record.user?.username}</Typography.Text>
                  <Typography.Text type="secondary" style={{ marginLeft: 8 }}>
                    {record.user?.accountName}
                    {record.user?.userId && (
                      <span style={{ marginLeft: 8 }}>ID: {record.user.userId}</span>
                    )}
                  </Typography.Text>
                </div>
              ),
            },
          ];

          // Only show Admin column for SuperAdmins
          if (isSuperAdmin) {
            baseColumns.push({
              title: "Admin",
              key: "admin",
              width: 120,
              render: (record: RequestData) => (
                <Tag color="purple">
                  {(record as any).admin?.username || 'N/A'}
                </Tag>
              ),
            });
          }

          const allColumns = [
            ...baseColumns,
          // Only show full status column for completed/rejected tab
          ...(activeTab === 'completed' ? [{
            title: t("requestsPanel.pendingRequestsTable.table.columns.status"),
            key: "status",
            width: 150,
            render: (record: RequestData) => (
              <Space size={4}>
                <Tag
                  color={
                    record.status === "PENDING"
                      ? "blue"
                      : record.status === "REQUIRES_VALIDATION"
                      ? "orange"
                      : record.status === "APPROVED"
                      ? "processing"
                      : record.status === "WAITING_FOR_CLIENT_VALIDATION"
                      ? "warning"
                      : record.status === "COMPLETED"
                      ? "success"
                      : record.status === "REJECTED"
                      ? "error"
                      : record.status === "CANCELED"
                      ? "default"
                      : record.status === "USER_NOT_FOUND"
                      ? "red"
                      : record.status === "DEPOSIT_ERROR"
                      ? "volcano"
                      : "default"
                  }
                >
                  {record.status === "REQUIRES_VALIDATION"
                    ? t("requestsPanel.pendingRequestsTable.tags.status.requiresValidation")
                    : record.status === "WAITING_FOR_CLIENT_VALIDATION"
                    ? t("requestsPanel.approvedWithdrawalsTable.tags.status.awaitingConfirmation")
                    : t(`requestsPanel.completedRejectedTable.tags.status.${record.status.toLowerCase()}`) ||
                      record.status}
                </Tag>
                <Tag color={record.type === "WITHDRAW" ? "#722ed1" : "#1890ff"}>
                  {t(`requestsPanel.pendingRequestsTable.tags.type.${record.type.toLowerCase()}`)}
                </Tag>
              </Space>
            ),
          }] : []),
          {
            title: t("requestsPanel.pendingRequestsTable.table.columns.amount"),
            key: "amount",
            width: 80,
            render: (record: RequestData) => {
              // For WITHDRAW requests
              if (record.type === "WITHDRAW" && record.withdrawAmount) {
                return `$${record.withdrawAmount.toFixed(2)}`;
              }

              // For LOAD requests with operation
              if (record.operation) {
                const baseAmount = record.operation.amount || 0;
                const bonusAmount = record.operation.bonus || 0;
                const totalAmount = baseAmount + bonusAmount;

                // Extract gamification details - prefer new gamificationResult field, fallback to legacy
                const gamificationData = record.gamificationResult || record.requestData?.gamification || record.operation?.requestData?.gamification;
                const legacyBonus = record.requestData?.input?.bonus || 0;
                const gamificationBonus = gamificationData?.bonus || 0;
                const totalPoints = gamificationData?.totalPoints || 0;
                const userProgress = gamificationData?.userProgress;
                const appliedRules = gamificationData?.appliedRules || [];
                const rejectedRules = gamificationData?.rejectedRules || [];
                const leveledUp = gamificationData?.leveledUp;

                return (
                  <Tooltip
                    title={
                      <div style={{ maxWidth: '300px' }}>
                        <div><strong>Base:</strong> ${baseAmount.toFixed(2)}</div>

                        {bonusAmount > 0 && (
                          <>
                            <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                              <strong>Bonus Total: ${bonusAmount.toFixed(2)}</strong>
                            </div>

                            {legacyBonus > 0 && (
                              <div style={{ fontSize: '12px', marginLeft: '8px' }}>
                                • Bonus Legacy: ${legacyBonus.toFixed(2)}
                              </div>
                            )}

                            {gamificationBonus > 0 && (
                              <>
                                <div style={{ fontSize: '12px', marginLeft: '8px' }}>
                                  • Bonus Gamificación: ${gamificationBonus.toFixed(2)}
                                </div>

                                {userProgress && (
                                  <div style={{ fontSize: '11px', marginLeft: '16px', marginTop: '4px', opacity: 0.9 }}>
                                    {userProgress.levelName && (
                                      <div>Nivel: {userProgress.levelName} ({userProgress.currentLevel})</div>
                                    )}
                                    {userProgress.currentStreak > 0 && (
                                      <div>Racha: {userProgress.currentStreak} días</div>
                                    )}
                                    {userProgress.totalLoads > 0 && (
                                      <div>Carga #{userProgress.totalLoads}</div>
                                    )}
                                  </div>
                                )}

                                {appliedRules.length > 0 && (
                                  <div style={{ fontSize: '11px', marginLeft: '8px', marginTop: '6px' }}>
                                    <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Reglas aplicadas:</div>
                                    {appliedRules.map((rule: any, idx: number) => {
                                      // Determinar el texto del cálculo
                                      let calculationText = '';

                                      if (rule.bonusType === 'PERCENTAGE') {
                                        calculationText = `${rule.bonusValue}% de $${rule.loadAmount?.toFixed(2)} = $${rule.bonusAmountBase?.toFixed(2)}`;
                                      } else if (rule.bonusType === 'FIXED_AMOUNT') {
                                        calculationText = `Monto fijo: $${rule.bonusValue?.toFixed(2)}`;
                                      } else if (rule.bonusType === 'POINTS') {
                                        calculationText = `${rule.bonusValue} puntos`;
                                      }

                                      const hasMultiplier = rule.levelMultiplier && rule.levelMultiplier !== 1.0;

                                      return (
                                        <div key={idx} style={{ marginBottom: '8px', paddingLeft: '8px', borderLeft: '2px solid rgba(255,255,255,0.3)' }}>
                                          <div style={{ fontWeight: 'bold', color: '#52c41a' }}>
                                            {rule.ruleName}
                                          </div>
                                          <div style={{ fontSize: '10px', opacity: 0.9, marginLeft: '4px' }}>
                                            └─ {calculationText}
                                          </div>
                                          {hasMultiplier && (
                                            <div style={{ fontSize: '10px', opacity: 0.9, marginLeft: '4px' }}>
                                              └─ Multiplicador nivel: ×{rule.levelMultiplier?.toFixed(2)} = ${rule.bonusAmount?.toFixed(2)}
                                            </div>
                                          )}
                                          {!hasMultiplier && (
                                            <div style={{ fontSize: '10px', opacity: 0.9, marginLeft: '4px', fontWeight: 'bold' }}>
                                              └─ Total: ${rule.bonusAmount?.toFixed(2)}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}

                                {leveledUp && (
                                  <div style={{ fontSize: '11px', marginLeft: '16px', marginTop: '4px', color: '#52c41a', fontWeight: 'bold' }}>
                                    ⭐ Subiste a {leveledUp.newLevelName}!
                                  </div>
                                )}

                                {totalPoints > 0 && (
                                  <div style={{ fontSize: '11px', marginLeft: '16px', marginTop: '4px', color: '#1890ff' }}>
                                    +{totalPoints} puntos ganados
                                  </div>
                                )}
                              </>
                            )}
                          </>
                        )}

                        {/* Resumen reglas rechazadas */}
                        {rejectedRules.length > 0 && (
                          <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                            <div style={{ fontSize: '11px', opacity: 0.8 }}>
                              <span style={{ color: '#ff4d4f' }}>✗</span> {rejectedRules.length} reglas no aplicadas
                            </div>
                          </div>
                        )}

                        <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(255,255,255,0.2)', fontWeight: 'bold' }}>
                          Total: ${totalAmount.toFixed(2)}
                        </div>

                        {/* Botón ver detalles */}
                        {gamificationData && (
                          <div style={{ marginTop: '8px', textAlign: 'center' }}>
                            <Button
                              type="link"
                              size="small"
                              style={{ padding: 0, fontSize: '11px' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setCtxGamificationData({ data: gamificationData, amount: baseAmount });
                                setCtxTab('gamif');
                                const menuWidth = 380;
                                const rect = (e.target as HTMLElement).getBoundingClientRect();
                                const x = Math.min(rect.left, window.innerWidth - menuWidth - 10);
                                const y = Math.min(rect.bottom + 4, window.innerHeight - 300);
                                setContextMenu({ visible: true, x, y, record });
                              }}
                            >
                              Ver detalles completos
                            </Button>
                          </div>
                        )}
                      </div>
                    }
                  >
                    <span style={{ cursor: gamificationData ? 'pointer' : 'default' }}>${totalAmount.toFixed(2)}</span>
                  </Tooltip>
                );
              }

              // If linked to transfer but no operation
              if (record.linkedTransfer?.amount) {
                return `$${record.linkedTransfer.amount.toFixed(2)}`;
              }

              return "$0.00";
            },
          },
          {
            title: t("requestsPanel.pendingRequestsTable.table.columns.date"),
            key: "date",
            width: 95,
            render: (record: RequestData) => {
              const date = new Date(record.processedAt || record.createdAt);
              return (
                <Tooltip title={date.toLocaleString()}>
                  <span style={{ fontSize: '12px' }}>
                    {date.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' })}
                    {' '}
                    {date.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </Tooltip>
              );
            },
          },
        ];

        // Only show Actions column for non-SuperAdmins
        if (!isSuperAdmin) {
          allColumns.push({
            title: t("requestsPanel.pendingRequestsTable.table.columns.actions"),
            key: "actions",
            width: 160,
            render: (record: RequestData) => renderActions(record),
          });
        }

        return allColumns;
        },
        [navigate, onCancelWithdraw, t, isProcessing, isSuperAdmin, activeTab]
      );

      const onRow = useCallback(
        (record: RequestData) => {
          return {
            onContextMenu: (event: React.MouseEvent) => {
              event.preventDefault();
              const menuWidth = 240;
              const menuHeight = 400;
              const x = Math.min(event.clientX, window.innerWidth - menuWidth);
              const y = Math.min(event.clientY, window.innerHeight - menuHeight);
              setContextMenu({
                visible: true,
                x,
                y,
                record,
              });
              setCtxTab('info');
              // Pre-populate gamification data from record if available
              const gData = record.gamificationResult || record.requestData?.gamification || record.operation?.requestData?.gamification;
              const baseAmt = record.operation?.amount || 0;
              setCtxGamificationData(gData ? { data: gData, amount: baseAmt } : null);
            },
          };
        },
        []
      );

      // Mobile card renderer
      const renderMobileCard = useCallback(
        (record: RequestData) => {
          const key = record.uniqueKey || record.id;

          const getFormattedAmount = () => {
            if (record.type === "WITHDRAW" && record.withdrawAmount) {
              return `$${record.withdrawAmount.toFixed(2)}`;
            }

            if (record.operation) {
              const baseAmount = record.operation.amount || 0;
              const bonusAmount = record.operation.bonus || 0;
              const totalAmount = baseAmount + bonusAmount;
              return `$${totalAmount.toFixed(2)}`;
            }

            if (record.linkedTransfer?.amount) {
              return `$${record.linkedTransfer.amount.toFixed(2)}`;
            }

            return "$0.00";
          };

          return (
            <Card
              key={key}
              style={{
                marginBottom: 12,
                borderRadius: 8,
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              }}
              styles={{
                body: {
                  padding: 12,
                },
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 8,
                }}
              >
                <Space>
                  <NumberOutlined style={{ color: "#1890ff" }} />
                  {record.requestNumber ? (
                    <Typography.Text copyable>{record.requestNumber}</Typography.Text>
                  ) : (
                    <Typography.Text type="secondary">{t("common.noId")}</Typography.Text>
                  )}
                </Space>
                <Space size={4}>
                  <Tag
                    color={
                      record.status === "PENDING"
                        ? "blue"
                        : record.status === "REQUIRES_VALIDATION"
                        ? "orange"
                        : record.status === "APPROVED"
                        ? "processing"
                        : record.status === "WAITING_FOR_CLIENT_VALIDATION"
                        ? "warning"
                        : record.status === "COMPLETED"
                        ? "success"
                        : record.status === "REJECTED"
                        ? "error"
                        : "default"
                    }
                  >
                    {record.status}
                  </Tag>
                  <Tag color={record.type === "WITHDRAW" ? "#722ed1" : "#1890ff"}>
                    {t(`requestsPanel.pendingRequestsTable.tags.type.${record.type.toLowerCase()}`)}
                  </Tag>
                </Space>
              </div>

              <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
                <UserOutlined style={{ marginRight: 8, color: "#722ed1" }} />
                <div>
                  <Typography.Text strong>{record.user?.username}</Typography.Text>
                  <div>
                    <Typography.Text type="secondary" style={{ fontSize: "0.85rem" }}>
                      {record.user?.accountName}
                      {record.user?.userId && <span style={{ marginLeft: 4 }}>ID: {record.user.userId}</span>}
                    </Typography.Text>
                  </div>
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 8,
                }}
              >
                <Space>
                  <DollarOutlined style={{ color: "#52c41a" }} />
                  {getFormattedAmount()}
                </Space>
                <Space>
                  <ClockCircleOutlined style={{ color: "#faad14" }} />
                  <Typography.Text type="secondary">
                    {new Date(record.createdAt).toLocaleString()}
                  </Typography.Text>
                </Space>
              </div>

              {record.type === "WITHDRAW" && (
                <div style={{ marginBottom: 8 }}>
                  <Space>
                    <CreditCardOutlined style={{ color: "#531dab" }} />
                    <Popover
                      placement="right"
                      content={
                        <Space direction="vertical" size={0} style={{ lineHeight: "1.2" }}>
                          <Typography.Text copyable={{ text: record.withdrawCvuCbu }}>
                            CBU: {record.withdrawCvuCbu}
                          </Typography.Text>
                          <Typography.Text copyable={{ text: record.withdrawAlias }}>
                            Alias: {record.withdrawAlias}
                          </Typography.Text>
                          <Typography.Text strong copyable={{ text: record.withdrawHolder }}>
                            Titular: {record.withdrawHolder}
                          </Typography.Text>
                        </Space>
                      }
                      trigger="click"
                    >
                      <Button type="text" size="small">
                        Ver detalles
                      </Button>
                    </Popover>
                  </Space>
                </div>
              )}

              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                {renderActions(record)}
              </div>
            </Card>
          );
        },
        [navigate, onCancelWithdraw, t, isProcessing]
      );
      // Define allowed statuses based on active tab
      const allowedStatuses: RequestStatus[] = useMemo(() => {
        if (activeTab === 'completed') {
          return ["COMPLETED", "REJECTED", "CANCELED"];
        }
        return [];
      }, [activeTab]);

      const hasData = combinedData.length > 0;
      const isAnyLoading = isLoading.pending || isLoading.approvedWithdraws || isLoading.completedRejected;

      return (
        <div className="unified-requests-table">
          {/* Tabs for category selection */}
          <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Tabs
              activeKey={activeTab}
              onChange={handleTabChange}
              items={[
                {
                  key: 'pending',
                  label: (
                    <span>
                      {t("requestsPanel.tabs.pending.title")} ({pagination.pending.total})
                    </span>
                  ),
                },
                {
                  key: 'approved',
                  label: (
                    <span>
                      {t("requestsPanel.tabs.withdraws.title")} ({pagination.approvedWithdraws.total})
                    </span>
                  ),
                },
                {
                  key: 'completed',
                  label: (
                    <span>
                      {t("requestsPanel.tabs.completed.title")} ({pagination.completedRejected.total})
                    </span>
                  ),
                },
              ]}
            />
            <TableFilters
              onFilter={handleFilter}
              allowedStatuses={allowedStatuses}
              tableType="pending"
              isVisible={true}
              onOpenDrawer={() => {}}
              activeTab={activeTab}
            />
          </div>

          {isMobile ? (
            <div style={{ padding: "0 8px" }}>
              {isAnyLoading ? (
                <>
                  {[1, 2, 3].map((i) => (
                    <Card key={i} style={{ marginBottom: 12 }}>
                      <Skeleton active paragraph={{ rows: 3 }} />
                    </Card>
                  ))}
                </>
              ) : hasData ? (
                <>
                  {combinedData.map((record) => renderMobileCard(record as unknown as RequestData))}
                  <div style={{ marginTop: 8, marginBottom: 72 }}>
                    <Typography.Text type="secondary">
                      {`${t("common.total")}: ${combinedData.length} ${t("common.items")}`}
                    </Typography.Text>
                  </div>
                </>
              ) : (
                <Empty description={t("common.noRecordsFound")} />
              )}
            </div>
          ) : (
            <GlassTable
              columns={columns as TableColumnsType<any>}
              dataSource={combinedData}
              rowKey={(record) => record.uniqueKey || record.id}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: totalCount,
                showSizeChanger: true,
                showTotal: (total) => `${t("common.total")}: ${total} ${t("common.items")}`,
                onChange: handlePaginationChange,
                onShowSizeChange: handlePaginationChange,
              }}
              loading={isAnyLoading}
              size="small"
              scroll={{ x: 880, y: "calc(100vh - 300px)" }}
              onRow={onRow}
              className="custom-table"
              locale={{
                emptyText: (
                  <div style={{ padding: "24px 0", textAlign: "center" }}>
                    <Typography.Text type="secondary">{t("common.noRecordsFound")}</Typography.Text>
                  </div>
                ),
                triggerDesc: t("table.triggerDesc") || "Click to sort descending",
                triggerAsc: t("table.triggerAsc") || "Click to sort ascending",
                cancelSort: t("table.cancelSort") || "Click to cancel sorting",
              }}
            />
          )}

          {/* Context Menu Panel */}
          {contextMenu.visible && contextMenu.record && (() => {
            const menuWidth = 380;
            const spaceRight = window.innerWidth - contextMenu.x;
            const spaceBelow = window.innerHeight - contextMenu.y;
            const posX = spaceRight >= menuWidth + 10 ? contextMenu.x : Math.max(10, contextMenu.x - menuWidth);
            const posY = spaceBelow >= 300 ? contextMenu.y : Math.max(10, contextMenu.y - 300);
            const maxH = spaceBelow >= 300 ? spaceBelow - 20 : contextMenu.y - 10;

            return (
              <div
                className="ctx-menu-panel"
                style={{
                  position: 'fixed',
                  left: posX,
                  top: posY,
                  zIndex: 1050,
                  width: menuWidth,
                  maxHeight: maxH,
                  overflowY: 'auto',
                  borderRadius: 10,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
                  background: theme === 'dark' ? '#1f1f1f' : '#fff',
                  border: `1px solid ${theme === 'dark' ? '#333' : '#e8e8e8'}`,
                }}
              >
                {/* Header */}
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '10px 14px', borderBottom: `1px solid ${theme === 'dark' ? '#333' : '#f0f0f0'}`,
                  position: 'sticky', top: 0, zIndex: 2,
                  background: theme === 'dark' ? '#1f1f1f' : '#fff',
                  borderRadius: '10px 10px 0 0',
                }}>
                  <Space size={8}>
                    <UserOutlined style={{ color: '#ac4949' }} />
                    <Typography.Text strong>{contextMenu.record.user?.username}</Typography.Text>
                    {contextMenu.record.user?.accountName && (
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        ({contextMenu.record.user.accountName})
                      </Typography.Text>
                    )}
                  </Space>
                  <Button
                    type="text"
                    size="small"
                    icon={<CloseOutlined />}
                    onClick={() => setContextMenu(prev => ({ ...prev, visible: false }))}
                    style={{ fontSize: 12 }}
                  />
                </div>

                {/* Tabs */}
                <Tabs
                  activeKey={ctxTab}
                  onChange={(key) => {
                    setCtxTab(key);
                    if (key === 'history' && !contextHistoryData && !isLoadingCtxHistory) {
                      handleCtxUpdateHistory();
                    }
                  }}
                  size="small"
                  centered
                  style={{ margin: 0 }}
                  tabBarStyle={{ margin: '0 14px', borderBottom: `1px solid ${theme === 'dark' ? '#333' : '#f0f0f0'}` }}
                  items={[
                    {
                      key: 'info',
                      label: <span style={{ fontSize: 12 }}><DollarOutlined /> Info</span>,
                      children: (
                        <div style={{ padding: '6px 14px 14px' }}>
                          {/* Balance */}
                          <div style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            padding: '8px 12px', marginBottom: 8,
                            background: theme === 'dark' ? '#141414' : '#fafafa',
                            borderRadius: 8,
                          }}>
                            <Space size={8}>
                              <DollarOutlined style={{ color: '#52c41a', fontSize: 16 }} />
                              <div>
                                <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Balance</Typography.Text>
                                {isLoadingBalance && contextBalance === null ? (
                                  <Spin size="small" />
                                ) : (
                                  <Typography.Text strong style={{ fontSize: 16 }}>
                                    ${contextBalance !== null ? contextBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 'N/A'}
                                  </Typography.Text>
                                )}
                              </div>
                            </Space>
                            <Button
                              type="text"
                              size="small"
                              icon={<ReloadOutlined spin={isLoadingBalance} />}
                              onClick={handleCtxRefreshBalance}
                              disabled={isLoadingBalance}
                            />
                          </div>

                          {/* Copy actions */}
                          <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                            <Button
                              size="small"
                              icon={<CopyOutlined />}
                              onClick={() => {
                                const u = contextMenu.record?.user?.username;
                                if (u) { navigator.clipboard.writeText(u); message.success('Username copiado'); }
                              }}
                              style={{ flex: 1 }}
                            >
                              Username
                            </Button>
                            <Button
                              size="small"
                              icon={<CopyOutlined />}
                              onClick={() => {
                                const uid = contextMenu.record?.user?.userId;
                                if (uid) { navigator.clipboard.writeText(String(uid)); message.success('User ID copiado'); }
                              }}
                              style={{ flex: 1 }}
                            >
                              User ID
                            </Button>
                          </div>
                          <Button
                            block
                            size="small"
                            icon={<CopyOutlined />}
                            onClick={handleCtxCopyCredentials}
                            style={{ marginBottom: 10 }}
                          >
                            Copiar Credenciales
                          </Button>

                          <Divider style={{ margin: '8px 0' }} />

                          {/* Rollover / Anti-lavado Section */}
                          {isLoadingRollover ? (
                            <div style={{ textAlign: 'center', padding: '8px 0' }}>
                              <Spin size="small" />
                              <Typography.Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>Cargando rollover...</Typography.Text>
                            </div>
                          ) : contextRollover?.status === 'active' ? (
                            <Space direction="vertical" style={{ width: '100%' }} size={6}>
                              <div style={{
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                padding: '6px 10px',
                                background: contextRollover.rolloverRequired > 0
                                  ? (theme === 'dark' ? '#2a1f00' : '#fffbe6')
                                  : (theme === 'dark' ? '#0a2e0a' : '#f6ffed'),
                                borderRadius: 6,
                                border: `1px solid ${contextRollover.rolloverRequired > 0 ? '#ffc53d' : '#b7eb8f'}`,
                              }}>
                                <div>
                                  <Typography.Text style={{ fontSize: 11, display: 'block' }} type="secondary">Rollover (Bono)</Typography.Text>
                                  {contextRollover.rolloverRequired > 0 ? (
                                    <Tag color="orange" style={{ margin: 0 }}>${contextRollover.rolloverRequired.toLocaleString()}</Tag>
                                  ) : (
                                    <Tag color="green" style={{ margin: 0 }}>Cumplido</Tag>
                                  )}
                                </div>
                                {contextRollover.rolloverRequired > 0 && (
                                  <Button danger size="small" icon={<StopOutlined />} onClick={handleCtxCancelRollover}>
                                    Cancelar
                                  </Button>
                                )}
                              </div>

                              <div style={{
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                padding: '6px 10px',
                                background: contextRollover.antiLavadoRequired > 0
                                  ? (theme === 'dark' ? '#2a1f00' : '#fffbe6')
                                  : (theme === 'dark' ? '#0a2e0a' : '#f6ffed'),
                                borderRadius: 6,
                                border: `1px solid ${contextRollover.antiLavadoRequired > 0 ? '#ffc53d' : '#b7eb8f'}`,
                              }}>
                                <div>
                                  <Typography.Text style={{ fontSize: 11, display: 'block' }} type="secondary">Cond. Retiro (Dep.)</Typography.Text>
                                  {contextRollover.antiLavadoRequired > 0 ? (
                                    <Tag color="orange" style={{ margin: 0 }}>${contextRollover.antiLavadoRequired.toLocaleString()}</Tag>
                                  ) : (
                                    <Tag color="green" style={{ margin: 0 }}>Cumplido</Tag>
                                  )}
                                </div>
                                {contextRollover.antiLavadoRequired > 0 && (
                                  <Button danger size="small" icon={<StopOutlined />} onClick={handleCtxCancelAntiLavado}>
                                    Cancelar
                                  </Button>
                                )}
                              </div>

                              <div style={{ display: 'flex', gap: 12, fontSize: 11, paddingTop: 2 }}>
                                <Typography.Text type="secondary">
                                  Dep: ${contextRollover.depositAmount?.toLocaleString() || '0'}
                                </Typography.Text>
                                <Typography.Text type="secondary">
                                  Bono: ${contextRollover.totalBonusAmount?.toLocaleString() || '0'}
                                </Typography.Text>
                              </div>
                            </Space>
                          ) : (
                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                              Sin rollover activo
                            </Typography.Text>
                          )}
                        </div>
                      ),
                    },
                    {
                      key: 'history',
                      label: <span style={{ fontSize: 12 }}><HistoryOutlined /> Historial</span>,
                      children: (
                        <div style={{ padding: '6px 14px 14px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                              {contextHistoryData ? `${contextHistoryData.filter((i: any) => Array.isArray(i) && i.length >= 7).length} registros` : 'Sin datos'}
                            </Typography.Text>
                            <Button
                              type="link"
                              size="small"
                              icon={<ReloadOutlined />}
                              onClick={handleCtxUpdateHistory}
                              loading={isLoadingCtxHistory}
                              style={{ fontSize: 11, padding: 0 }}
                            >
                              {contextHistoryData ? 'Refresh' : 'Cargar'}
                            </Button>
                          </div>

                          {isLoadingCtxHistory && !contextHistoryData && (
                            <div style={{ textAlign: 'center', padding: '20px 0' }}>
                              <Spin size="small" />
                            </div>
                          )}

                          {contextHistoryData && contextHistoryData.length > 0 && (
                            <div style={{
                              maxHeight: 300, overflowY: 'auto',
                              border: `1px solid ${theme === 'dark' ? '#333' : '#f0f0f0'}`,
                              borderRadius: 6,
                            }}>
                              <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                                <thead>
                                  <tr style={{
                                    background: theme === 'dark' ? '#141414' : '#fafafa',
                                    position: 'sticky', top: 0,
                                  }}>
                                    <th style={{ padding: '4px 6px', textAlign: 'left', fontWeight: 600 }}>Fecha</th>
                                    <th style={{ padding: '4px 6px', textAlign: 'left', fontWeight: 600 }}>Op</th>
                                    <th style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 600 }}>Monto</th>
                                    <th style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 600 }}>Saldo</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {contextHistoryData
                                    .filter((item: any) => Array.isArray(item) && item.length >= 7)
                                    .slice(0, 30)
                                    .map((item: any, idx: number) => {
                                      const date = new Date(item[0]);
                                      const dateStr = `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
                                      return (
                                        <tr key={idx} style={{
                                          borderBottom: `1px solid ${theme === 'dark' ? '#2a2a2a' : '#f5f5f5'}`,
                                        }}>
                                          <td style={{ padding: '3px 6px', whiteSpace: 'nowrap' }}>{dateStr}</td>
                                          <td style={{ padding: '3px 6px', whiteSpace: 'nowrap' }}>
                                            <Typography.Text style={{ fontSize: 11 }}>{item[3]}</Typography.Text>
                                          </td>
                                          <td style={{ padding: '3px 6px', textAlign: 'right', whiteSpace: 'nowrap' }}>{item[4]}</td>
                                          <td style={{ padding: '3px 6px', textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 600 }}>{item[6]}</td>
                                        </tr>
                                      );
                                    })}
                                </tbody>
                              </table>
                            </div>
                          )}

                          {contextHistoryData && contextHistoryData.length === 0 && (
                            <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', textAlign: 'center', padding: '20px 0' }}>
                              Sin historial disponible
                            </Typography.Text>
                          )}

                          {!contextHistoryData && !isLoadingCtxHistory && (
                            <div style={{ textAlign: 'center', padding: '20px 0' }}>
                              <Button
                                size="small"
                                icon={<HistoryOutlined />}
                                onClick={handleCtxUpdateHistory}
                              >
                                Cargar Historial
                              </Button>
                            </div>
                          )}
                        </div>
                      ),
                    },
                    {
                      key: 'notif',
                      label: (() => {
                        const rec = contextMenu.record;
                        let logCount = 0;
                        let hasErr = false;
                        try {
                          const raw = rec?.notificationLogs;
                          const parsed = typeof raw === 'string' ? JSON.parse(raw) : Array.isArray(raw) ? raw : raw ? [raw] : [];
                          logCount = parsed.length;
                          hasErr = parsed.some((l: any) => !l.success);
                        } catch {}
                        return (
                          <span style={{ fontSize: 12 }}>
                            <BellOutlined /> Notif.
                            {logCount > 0 && (
                              <span style={{
                                display: 'inline-block', marginLeft: 4, width: 6, height: 6, borderRadius: '50%',
                                background: hasErr ? '#ff4d4f' : '#52c41a',
                              }} />
                            )}
                          </span>
                        );
                      })(),
                      children: (() => {
                        const rec = contextMenu.record;
                        let logs: any[] = [];
                        try {
                          const raw = rec?.notificationLogs;
                          if (typeof raw === 'string') logs = JSON.parse(raw);
                          else if (Array.isArray(raw)) logs = raw;
                          else if (raw) logs = [raw];
                        } catch {}

                        const userNotifs = logs.filter((l: any) => l.category === 'USER_NOTIFICATION');
                        const convLogs = logs.filter((l: any) => l.category === 'CONVERSION_TRACKING');

                        const renderEntry = (log: any, idx: number) => (
                          <div key={idx} style={{
                            padding: '8px 10px', marginBottom: 6,
                            background: theme === 'dark' ? '#141414' : '#fafafa',
                            borderRadius: 6,
                            borderLeft: `3px solid ${log.success ? '#52c41a' : '#ff4d4f'}`,
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                              <Typography.Text strong style={{ fontSize: 11 }}>
                                {log.service === 'Whapify' ? 'Whapify (Usuario)' : log.service === 'Facebook' ? 'Facebook Pixel' : log.service}
                              </Typography.Text>
                              {log.attemptNumber > 1 && (
                                <Tag color="blue" style={{ fontSize: 10, padding: '0 4px', lineHeight: '16px', margin: 0 }}>
                                  #{log.attemptNumber}
                                </Tag>
                              )}
                              {log.success ? (
                                <Tag color="success" style={{ fontSize: 10, padding: '0 4px', lineHeight: '16px', margin: 0 }}>OK</Tag>
                              ) : (
                                <Tag color="error" style={{ fontSize: 10, padding: '0 4px', lineHeight: '16px', margin: 0 }}>Error</Tag>
                              )}
                            </div>
                            {log.humanDescription && (
                              <Typography.Text style={{ fontSize: 11, display: 'block', marginBottom: 2 }} type="secondary">
                                {log.humanDescription}
                              </Typography.Text>
                            )}
                            {!log.success && (
                              <Typography.Text style={{ fontSize: 11, display: 'block', color: '#ff4d4f' }}>
                                {log.error || log.message || 'Error desconocido'}
                              </Typography.Text>
                            )}
                            <Typography.Text type="secondary" style={{ fontSize: 10, display: 'block', marginTop: 4 }}>
                              {new Date(log.timestamp).toLocaleString('es-AR')}
                            </Typography.Text>
                          </div>
                        );

                        return (
                          <div style={{ padding: '6px 14px 14px' }}>
                            {logs.length === 0 ? (
                              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                                <BellOutlined style={{ fontSize: 24, opacity: 0.2, display: 'block', marginBottom: 8 }} />
                                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                  Sin notificaciones
                                </Typography.Text>
                              </div>
                            ) : (
                              <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                                {userNotifs.length > 0 && (
                                  <>
                                    <Typography.Text type="secondary" style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, display: 'block', marginBottom: 6 }}>
                                      Usuario (Whapify)
                                    </Typography.Text>
                                    {userNotifs.map(renderEntry)}
                                  </>
                                )}
                                {convLogs.length > 0 && (
                                  <>
                                    <Typography.Text type="secondary" style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, display: 'block', marginBottom: 6, marginTop: userNotifs.length > 0 ? 8 : 0 }}>
                                      Tracking (Facebook)
                                    </Typography.Text>
                                    {convLogs.map(renderEntry)}
                                  </>
                                )}
                                {userNotifs.length === 0 && convLogs.length === 0 && logs.map(renderEntry)}
                                <Typography.Text type="secondary" style={{ fontSize: 10, display: 'block', textAlign: 'center', marginTop: 6 }}>
                                  {logs.length} {logs.length === 1 ? 'registro' : 'registros'}
                                </Typography.Text>
                              </div>
                            )}
                          </div>
                        );
                      })(),
                    },
                    {
                      key: 'gamif',
                      label: <span style={{ fontSize: 12 }}><CheckCircleOutlined /> Gamif.</span>,
                      children: (() => {
                        const gd = ctxGamificationData?.data;
                        if (!gd) return (
                          <div style={{ padding: '20px 14px', textAlign: 'center' }}>
                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                              Sin datos de gamificación para esta solicitud
                            </Typography.Text>
                          </div>
                        );
                        return (
                          <div style={{ padding: '6px 14px 14px', maxHeight: 400, overflowY: 'auto' }}>
                            {/* Summary */}
                            <div style={{
                              padding: '8px 10px', marginBottom: 8,
                              background: theme === 'dark' ? '#141414' : '#fafafa',
                              borderRadius: 6,
                            }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                                <span>Monto carga:</span>
                                <strong>${ctxGamificationData.amount?.toFixed(2)}</strong>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                                <span>Bonus:</span>
                                <strong style={{ color: '#52c41a' }}>${gd.bonus?.toFixed(2) || '0.00'}</strong>
                              </div>
                              {gd.totalPoints > 0 && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                                  <span>Puntos:</span>
                                  <strong style={{ color: '#1890ff' }}>+{gd.totalPoints}</strong>
                                </div>
                              )}
                            </div>

                            {/* Level Up */}
                            {gd.leveledUp && (
                              <div style={{
                                textAlign: 'center', padding: '8px', marginBottom: 8,
                                background: theme === 'dark' ? '#0a2e0a' : '#f6ffed',
                                borderRadius: 6, border: '1px solid #b7eb8f',
                              }}>
                                <div style={{ color: '#52c41a', fontWeight: 'bold', fontSize: 12 }}>
                                  Nivel {gd.leveledUp.oldLevel} → {gd.leveledUp.newLevel} ({gd.leveledUp.newLevelName})
                                </div>
                              </div>
                            )}

                            {/* User Progress */}
                            {gd.userProgress && (
                              <div style={{
                                padding: '8px 10px', marginBottom: 8,
                                background: theme === 'dark' ? '#141414' : '#fafafa',
                                borderRadius: 6, fontSize: 11,
                              }}>
                                <Typography.Text type="secondary" style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, display: 'block', marginBottom: 6 }}>
                                  Progreso
                                </Typography.Text>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px' }}>
                                  <span><strong>Nivel:</strong> {gd.userProgress.levelName} ({gd.userProgress.currentLevel})</span>
                                  <span><strong>Racha:</strong> {gd.userProgress.currentStreak}d</span>
                                  <span><strong>Cargas:</strong> {gd.userProgress.totalLoads}</span>
                                  <span><strong>Bonus total:</strong> ${gd.userProgress.lifetimeBonus?.toFixed(2)}</span>
                                </div>
                              </div>
                            )}

                            {/* Evaluation Context */}
                            {gd.evaluationContext && !gd.userProgress && (
                              <div style={{
                                padding: '8px 10px', marginBottom: 8,
                                background: theme === 'dark' ? '#141414' : '#fafafa',
                                borderRadius: 6, fontSize: 11,
                              }}>
                                <Typography.Text type="secondary" style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, display: 'block', marginBottom: 6 }}>
                                  Contexto
                                </Typography.Text>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px' }}>
                                  <span><strong>Nivel:</strong> {gd.evaluationContext.levelName} ({gd.evaluationContext.level})</span>
                                  <span><strong>Racha:</strong> {gd.evaluationContext.streak}d</span>
                                  <span><strong>Cargas:</strong> {gd.evaluationContext.totalLoads}</span>
                                </div>
                              </div>
                            )}

                            {/* Applied Rules */}
                            {gd.appliedRules?.length > 0 && (
                              <>
                                <Typography.Text type="secondary" style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, display: 'block', marginBottom: 6 }}>
                                  Reglas aplicadas ({gd.appliedRules.length})
                                </Typography.Text>
                                {gd.appliedRules.map((rule: any, idx: number) => (
                                  <div key={idx} style={{
                                    padding: '6px 10px', marginBottom: 4,
                                    borderLeft: '3px solid #52c41a',
                                    background: theme === 'dark' ? '#0a2e0a' : '#f6ffed',
                                    borderRadius: '0 6px 6px 0', fontSize: 11,
                                  }}>
                                    <div style={{ fontWeight: 600, color: '#52c41a' }}>{rule.ruleName}</div>
                                    <div style={{ fontSize: 11, marginTop: 2 }}>
                                      {rule.bonusAmount > 0 && <span>+${rule.bonusAmount?.toFixed(2)}</span>}
                                      {rule.bonusAmount > 0 && rule.pointsAmount > 0 && <span> | </span>}
                                      {rule.pointsAmount > 0 && <span>+{rule.pointsAmount} pts</span>}
                                    </div>
                                  </div>
                                ))}
                              </>
                            )}

                            {/* Rejected Rules */}
                            {gd.rejectedRules?.length > 0 && (
                              <>
                                <Typography.Text type="secondary" style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, display: 'block', marginTop: 8, marginBottom: 6 }}>
                                  No aplicadas ({gd.rejectedRules.length})
                                </Typography.Text>
                                {gd.rejectedRules.map((rule: any, idx: number) => (
                                  <div key={idx} style={{
                                    padding: '6px 10px', marginBottom: 4,
                                    borderLeft: '3px solid #ff4d4f',
                                    background: theme === 'dark' ? '#2a1215' : '#fff2f0',
                                    borderRadius: '0 6px 6px 0', fontSize: 11,
                                  }}>
                                    <div style={{ fontWeight: 600, color: '#ff4d4f' }}>{rule.ruleName}</div>
                                    <div style={{ marginTop: 2, opacity: 0.7 }}>{rule.reason}</div>
                                  </div>
                                ))}
                              </>
                            )}

                            {/* Messages */}
                            {gd.messages?.length > 0 && (
                              <>
                                <Typography.Text type="secondary" style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, display: 'block', marginTop: 8, marginBottom: 6 }}>
                                  Mensajes ({gd.messages.length})
                                </Typography.Text>
                                {gd.messages.map((msg: string, idx: number) => (
                                  <div key={idx} style={{
                                    padding: '6px 10px', marginBottom: 4,
                                    background: theme === 'dark' ? '#141414' : '#fafafa',
                                    borderRadius: 6, fontSize: 11,
                                    borderLeft: '3px solid #1890ff',
                                    whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                                  }}>
                                    {msg}
                                  </div>
                                ))}
                              </>
                            )}
                          </div>
                        );
                      })(),
                    },
                  ]}
                />
              </div>
            );
          })()}

          {/* Panel Selector for context menu operations */}
          <PanelSelector
            isOpen={panelSelectorOpen}
            onClose={() => setPanelSelectorOpen(false)}
            onPanelSelect={handleCtxPanelSelect}
            panels={availablePanels}
            loading={isLoadingPanels}
            actionTitle={
              pendingPanelOperation.type === 'balance'
                ? 'Verificar saldo'
                : pendingPanelOperation.type === 'history'
                ? 'Verificar historial'
                : 'Seleccionar panel'
            }
            actionDescription={
              pendingPanelOperation.type === 'balance'
                ? 'Selecciona el panel para verificar el saldo del usuario.'
                : pendingPanelOperation.type === 'history'
                ? 'Selecciona el panel para verificar el historial del usuario.'
                : 'Selecciona un panel para continuar.'
            }
          />
        </div>
      );
    }
  );

UnifiedRequestsTable.displayName = "UnifiedRequestsTable";
